import { Link } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import {
  Home,
  BookOpen,
  Video,
  User,
  BarChart3,
  Users,
  GraduationCap,
  LogOut,
  Settings,
  Calendar,
  FolderTree,
  Shield,
  UserCog,
  UserPlus,
  FileCheck
} from 'lucide-react';
import { useAuth } from '@/contexts/auth-context';
import { Button } from '@/components/ui/button';
import { RoleSwitcher } from './role-switcher';

export function Sidebar() {
  const location = useLocation();
  const pathname = location.pathname;
  const { profile, signOut } = useAuth();

  const getNavItems = () => {
    if (profile?.role === 'admin') {
      return [
        { href: '/dashboard', label: 'Dashboard', icon: Home },
        { href: '/dashboard/courses', label: 'Gestione Corsi', icon: BookOpen },
        { href: '/dashboard/users', label: 'Gestione Utenti', icon: Users },
        { href: '/dashboard/teacher-requests', label: 'Richieste Docenti', icon: FileCheck },
        { href: '/dashboard/payments', label: 'Pagamenti', icon: BarChart3 },
        { href: '/dashboard/categories', label: 'Categorie', icon: FolderTree },
        { href: '/dashboard/privacy', label: 'Privacy', icon: Shield },
        { href: '/dashboard/settings', label: 'Impostazioni', icon: Settings },
        { href: '/dashboard/account', label: 'Account', icon: UserCog },
      ];
    }
    if (profile?.role === 'docente') {
      return [
        { href: '/dashboard', label: 'Dashboard', icon: Home },
        { href: '/dashboard/my-courses', label: 'I miei corsi', icon: BookOpen },
        { href: '/dashboard/create-course', label: 'Crea corso', icon: GraduationCap },
        { href: '/dashboard/live', label: 'Live sessions', icon: Video },
        { href: '/dashboard/earnings', label: 'Guadagni', icon: BarChart3 },
        { href: '/dashboard/profile', label: 'Profilo', icon: User },
        { href: '/dashboard/account', label: 'Account', icon: UserCog },
      ];
    }
    const studentItems = [
      { href: '/dashboard', label: 'Home', icon: Home },
      { href: '/dashboard/courses', label: 'Catalogo corsi', icon: BookOpen },
      { href: '/dashboard/calendar', label: 'Calendario', icon: Calendar },
      { href: '/dashboard/my-learning', label: 'I miei corsi', icon: Video },
    ];

    if (!profile?.is_teacher) {
      studentItems.push({ href: '/dashboard/become-teacher', label: 'Diventa Docente', icon: UserPlus });
    }

    studentItems.push(
      { href: '/dashboard/profile', label: 'Profilo', icon: User },
      { href: '/dashboard/account', label: 'Account', icon: UserCog }
    );

    return studentItems;
  };

  const navItems = getNavItems();

  return (
    <aside className="hidden md:flex md:flex-col md:fixed md:inset-y-0 md:w-64 bg-white border-r border-gray-200">
      <div className="flex flex-col flex-1 min-h-0">
        <div className="flex items-center h-16 px-4 border-b border-gray-200">
          <GraduationCap className="w-8 h-8 text-blue-600" />
          <span className="ml-2 text-xl font-bold text-gray-900">LMS Platform</span>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = pathname === item.href || pathname?.startsWith(item.href + '/');
            return (
              <Link
                key={item.href}
                to={item.href}
                className={cn(
                  'flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors',
                  isActive
                    ? 'bg-blue-50 text-blue-700'
                    : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                )}
              >
                <Icon className="w-5 h-5 mr-3" />
                {item.label}
              </Link>
            );
          })}
        </nav>
        <div className="p-4 border-t border-gray-200">
          <div className="mb-3">
            <RoleSwitcher />
          </div>
          <div className="flex items-center mb-3">
            <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-semibold">
              {profile?.full_name?.[0]?.toUpperCase() || 'U'}
            </div>
            <div className="ml-3 flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">
                {profile?.full_name || 'Utente'}
              </p>
              <p className="text-xs text-gray-500 capitalize">{profile?.role}</p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="w-full justify-start"
            onClick={signOut}
          >
            <LogOut className="w-4 h-4 mr-2" />
            Esci
          </Button>
        </div>
      </div>
    </aside>
  );
}
